import { Messages } from '@dojo/framework/i18n/i18n';
import { publish } from '@holmescorp/pub-sub';

import svc from './svc';

interface CertificateSetupData {
	messages: Messages;
	learnerName: string;
	completed_date: string;
	canvas?: HTMLCanvasElement;
	printNode?: HTMLIFrameElement;
}

const _imageObj = new Image();
const data: CertificateSetupData = {
	messages: {},
	learnerName: '',
	completed_date: ''
};

export const load_cert = (params: CertificateSetupData) => {
	data.messages = params.messages;
	data.learnerName = params.learnerName;
	data.completed_date = params.completed_date;
	data.canvas = params.canvas;
	data.printNode = params.printNode;
	(_imageObj as any).src = data.messages.background;
};

(_imageObj as any).addEventListener('load', () => {
	const toInt = (v: string | number): number => (typeof v === 'string' ? parseInt(v, 10) : v);

	const canvas = data.canvas as HTMLCanvasElement;
	const context = canvas.getContext('2d') as CanvasRenderingContext2D;

	const bgWidth = toInt(data.messages[`bg_width`]) || 0;
	const bgHeight = toInt(data.messages[`bg_height`]) || 0;
	const portrait = data.messages[`portrait`] === 'true';
	const textAlign = (data.messages[`text_align`] || 'center') as CanvasTextAlign;
	const nameFont = data.messages[`name_font`] || '32px sans-serif';
	const nameX = toInt(data.messages[`name_x`]) || 0;
	const nameY = toInt(data.messages[`name_y`]) || 0;
	const dateShow = data.messages[`date_show`] === 'true';
	const dateFont = data.messages[`date_font`] || '20px sans-serif';
	const dateX = toInt(data.messages[`date_x`]) || 0;
	const dateY = toInt(data.messages[`date_y`]) || 0;
	const maxNameLength = toInt(data.messages[`max_name_length`]) || 0;
	const nameYOffset = toInt(data.messages[`name_y_offset`]) || 0;

	if (portrait) {
		canvas.width = bgWidth;
		canvas.height = bgHeight;
	} else {
		// Swap height/width for landscape.
		canvas.width = bgHeight;
		canvas.height = bgWidth;

		// shift and rotate canvas to print landscape sideways
		context.translate(bgHeight, 0);
		context.rotate((90 * Math.PI) / 180);
	}

	context.drawImage(_imageObj as any, 0, 0);
	context.textAlign = textAlign;
	context.font = nameFont;
	if (maxNameLength > 0 && data.learnerName.length > maxNameLength) {
		const spaceIndex = data.learnerName.indexOf(' ');
		const firstName = spaceIndex !== -1 ? data.learnerName.slice(0, spaceIndex) : data.learnerName;
		const lastName = spaceIndex !== -1 ? data.learnerName.slice(spaceIndex + 1) : '';
		context.fillText(firstName, nameX, nameY);
		if (lastName) {
			context.fillText(lastName, nameX, nameY + nameYOffset);
		}
	} else {
		context.fillText(data.learnerName, nameX, nameY);
	}
	if (dateShow) {
		context.font = dateFont;
		context.fillText(data.completed_date, dateX, dateY);
	}

	const printNode = data.printNode as any;
	if (printNode) {
		const printDoc = printNode.contentDocument as Document;
		const newDoc = document.implementation.createHTMLDocument('') as Document;
		const certImg = newDoc.createElement('img') as HTMLImageElement;
		certImg.src = canvas.toDataURL();
		newDoc.body.appendChild(certImg);

		const style = newDoc.createElement('style') as HTMLStyleElement;
		const cssText = newDoc.createTextNode(
			'@page {size: letter portrait; margin: 0;} body{margin: .78in .6in;} img{width: 99%;}'
		);
		style.type = 'text/css';
		style.appendChild(cssText);
		(newDoc.head as HTMLElement).appendChild(style);
		const printEl = newDoc.documentElement as HTMLElement;

		const newNode = printDoc.importNode(printEl, true) as HTMLElement;
		printDoc.replaceChild(newNode, printDoc.documentElement as HTMLElement);
		setTimeout(() => {
			publish('cert/loaded');
		}, 1000);
	}
});

export const print_cert = () => {
	const printWin = (data.printNode as any).contentWindow as Window;

	printWin.focus();
	printWin.print();
};

export const check_rpc_passed = async (userid: string): Promise<boolean> => {
	const postTestData = await svc.posttest_passed({
		member_id: userid,
		session_id: window.hcstatic.course_session?.pk
	});

	return postTestData ? postTestData[Object.keys(postTestData)[0]]?.passed : false;
};

export const check_rpc_passed_date = async (userid: string): Promise<string | null> => {
	const postTestData = await svc.posttest_passed({
		member_id: userid,
		session_id: window.hcstatic.course_session?.pk
	});

	const details = postTestData ? postTestData[Object.keys(postTestData)[0]]?.details : null;
	const completed_date = details ? details[Object.keys(details)[0]]?.completed || null : null;

	return completed_date;
};

export const excerpt = (message: string, length: number): string => {
	const e = document.createElement('div');
	e.innerHTML = message;
	const msg = e.textContent;
	const dots = msg!.length > length ? '...' : '';
	return `${msg?.split(/\s+/).join(' ').slice(0, length)}${dots}`;
};
